"use client";
import { ReserveModalProvider } from "@/features/reserve/hooks/useReserveModal";
import { ThemeProvider } from "@gettactic/components/themes";
import { cn } from "@gettactic/helpers";
import { useMobileAppMessages } from "@gettactic/helpers/src/webview/useMobileAppMessages";
import {
  MS_1_MINUTE,
  useAuthenticated,
  useOrgFeatureFlag,
  useOrganizationSettings
} from "@gettactic/hooks";
import {
  SidebarBorderTransparent,
  SidebarBorderVisible,
  TRANSPARENT_SIDEBAR_BORDER_EXCEPT_DASHBOARD
} from "@gettactic/components/src/data-display/Card/classes";
import { MARIN_ROUTES, SETTINGS_ROUTES } from "@gettactic/defines/src/web-routes";
import { buildUrl } from "@gettactic/helpers";
import { usePathname } from "next/navigation";
import {
  AskTessaContext,
  AskTessaProvider
} from "@cmp-src/tessa/AskTessaProvider";
import {
  GlobalSearch,
  GlobalSearchProvider,
  useGlobalSearch
} from "@/features/global-search";
import { MapModalProvider } from "@/features/map/components/modal";
import { MainSidebar } from "@/ui/layout/MainSidebar";
import { applyMapSpotColors } from "@/ui/mapSpotColors";
import {
  applySimplifiedThemeColors,
  applyThemeFont,
  extractSimplifiedColors
} from "@/ui/theme";
import { AskTessa } from "@/features/tessa/components/AskTessa";
import { ImpersonateProvider } from "@/providers/ImpersonateProvider";
import { LayoutProvider } from "@/providers/LayoutProvider/LayoutProvider";
import { useFeatureAnnouncement } from "@/providers/FeatureAnnouncementProvider";
import { ReserveProvider } from "@/features/reserve/providers/ReserveProvider";
import { useRouter } from "@/lib/useRouter";
import {
  FeatureActionProvider,
  FeatureAnnouncementModal
} from "@gettactic/components/src/overlay/modals/FeatureAnnouncementModal";
import React from "react";

const classes = {
  Wrapper: "app-main-layout flex h-screen w-full",
  // Border color is applied dynamically (see `sidebarBorderColor`); the width
  // (`md:border-r`) stays constant so toggling the color never shifts layout.
  Sidebar:
    "app-main-layout-sidebar md:border-r bg-v2-sidebar text-v2-sidebar-foreground",
  MainContent: "app-main-layout-content flex-1"
};

export const MainLayoutInner: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const router = useRouter();
  useMobileAppMessages((message) => {
    switch (message.action) {
      case "navigate":
        switch (message.screen) {
          case "user-settings":
            router.push(buildUrl(SETTINGS_ROUTES.profile));
            break;
        }
        break;

      case "openUrl":
        window.open(message.url, "_blank");
        break;
    }
  });
  return <div className={classes.MainContent}>{children}</div>;
};

export const AskTessaFeatureFlagContainer: React.FC<
  React.PropsWithChildren<{
    isEnabled?: boolean;
  }>
> = ({ isEnabled, children }) => {
  if (!isEnabled) return children;

  return <AskTessaProvider>{children}</AskTessaProvider>;
};

const GlobalSearchWrapper: React.FC = () => {
  const { isOpen, close } = useGlobalSearch();
  return <GlobalSearch isOpen={isOpen} onClose={close} />;
};

const FeatureAnnouncementWrapper: React.FC = () => {
  const { open: openGlobalSearch } = useGlobalSearch();
  const router = useRouter();
  const {
    isModalOpen,
    currentFeature,
    allFeatures,
    modalMode,
    handleCloseModal,
    handleDismissFeature
  } = useFeatureAnnouncement();

  return (
    <FeatureActionProvider
      handlers={{
        onOpenGlobalSearch: openGlobalSearch,
        onRedirect: (url) => router.push(url)
      }}
    >
      <FeatureAnnouncementModal
        isOpen={isModalOpen}
        slide={modalMode === "single" ? currentFeature : undefined}
        slides={
          modalMode === "browse-all" ? (allFeatures ?? undefined) : undefined
        }
        onDismiss={handleDismissFeature}
        onClose={handleCloseModal}
        showConfetti={modalMode === "single"}
      />
    </FeatureActionProvider>
  );
};

export const AppMainLayout: React.FC<React.PropsWithChildren> = ({
  children
}) => {
  const tessaFeatureFlag = useOrgFeatureFlag("tessa");
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organizationId = authenticatedUser?.organization?.id;

  const { data: organizationSettings } = useOrganizationSettings({
    id: organizationId,
    refetchInterval: MS_1_MINUTE
  });

  // Keep the sidebar's right border solid on the dashboard; elsewhere render it
  // transparent (width preserved, so no layout shift). Disable the flag to keep
  // a solid border everywhere.
  const pathname = usePathname();
  const isDashboard = pathname === MARIN_ROUTES.dashboard;
  const sidebarBorderColor =
    !TRANSPARENT_SIDEBAR_BORDER_EXCEPT_DASHBOARD || isDashboard
      ? SidebarBorderVisible
      : SidebarBorderTransparent;

  React.useEffect(() => {
    if (organizationSettings?.theme_settings?.colors) {
      const simplified = extractSimplifiedColors(
        organizationSettings.theme_settings.colors
      );
      applySimplifiedThemeColors(simplified);
    }

    if (organizationSettings?.theme_settings?.font) {
      applyThemeFont(organizationSettings.theme_settings.font);
    }

    // Always call, even when map_theme_settings is absent — clearing
    // stale colors from a previous org/settings state matters just as much
    // as applying new ones (applyMapSpotColors clears before re-applying).
    applyMapSpotColors(organizationSettings?.map_theme_settings);
  }, [organizationSettings]);

  return (
    <ThemeProvider
      defaultBrand="base"
      storageKey="ui-theme-new"
      externalBrand="base"
    >
      <GlobalSearchProvider>
        <LayoutProvider>
          <ReserveProvider>
            <ReserveModalProvider>
              <MapModalProvider>
                <ImpersonateProvider>
                  <AskTessaFeatureFlagContainer
                    isEnabled={tessaFeatureFlag || false}
                  >
                    <AskTessaContext.Consumer>
                      {(askTessa) => {
                        const isChatDocked =
                          askTessa?.tessaDisclosure?.isOpen &&
                          !!askTessa?.dockedSide &&
                          askTessa?.isPopup.state;

                        return (
                          <div
                            className={cn(
                              "transition-[margin]",
                              isChatDocked &&
                                "relative isolate flex h-screen flex-col",
                              isChatDocked &&
                                askTessa?.dockedSide === "left" &&
                                "ml-[415px]",
                              isChatDocked &&
                                askTessa?.dockedSide === "right" &&
                                "mr-[415px]"
                            )}
                          >
                            {tessaFeatureFlag && <AskTessa />}
                            <div className={classes.Wrapper}>
                              <div
                                className={cn(
                                  classes.Sidebar,
                                  sidebarBorderColor
                                )}
                              >
                                <MainSidebar />
                              </div>
                              <MainLayoutInner>{children}</MainLayoutInner>
                            </div>
                            <GlobalSearchWrapper />
                            <FeatureAnnouncementWrapper />
                          </div>
                        );
                      }}
                    </AskTessaContext.Consumer>
                  </AskTessaFeatureFlagContainer>
                </ImpersonateProvider>
              </MapModalProvider>
            </ReserveModalProvider>
          </ReserveProvider>
        </LayoutProvider>
      </GlobalSearchProvider>
    </ThemeProvider>
  );
};
